import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../../context/auth-context'
import { useOutletContext } from 'react-router-dom'
import axios from 'axios'
import { rootUrl, formatToZMW } from '../../helpers'
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js'
import { 
  FaDollarSign, 
  FaReceipt, 
  FaFileInvoice, 
  FaBuilding, 
  FaArrowUp, 
  FaArrowDown,
  FaUsers,
  FaChartLine,
  FaSpinner,
  FaExclamationTriangle,
  FaCheckCircle
} from 'react-icons/fa'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

function RevenueDashboard() {
  const { user } = useAuth()
  const { allData } = useOutletContext()
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState({
    totals: {
      totalIncome: 0,
      totalExpenses: 0,
      totalBills: 0,
      totalReceipts: 0,
      totalProperties: 0,
      propertiesWithCoordinates: 0,
      propertyTypeCounts: { RES: 0, COM: 0, IND: 0, OTH: 0 },
      outstanding: 0,
      collectionRate: 0,
      recentReceipts: { count: 0, amount: 0 },
      recentBills: { count: 0, amount: 0 }
    },
    monthly: { income: Array(12).fill(0), bills: Array(12).fill(0), receipts: Array(12).fill(0) },
    meta: { year: new Date().getFullYear() }
  })

  // Fetch all dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        const res = await axios.get(rootUrl(`/revenueDashboardTotals/${user.id}`))
        setDashboardData(res.data.data)
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [user.id])

  // Calculate key metrics
  const metrics = useMemo(() => {
    const safeMonthly = dashboardData.monthly || { income: [], bills: [], receipts: [] }
    const safeTotals = dashboardData.totals || {}
    const currentYear = dashboardData.meta?.year || new Date().getFullYear()
    const monthlyData = Array.from({ length: 12 }, (_, month) => {
      const monthIncome = safeMonthly.income?.[month] || 0
      const monthBills = safeMonthly.bills?.[month] || 0
      const monthReceipts = safeMonthly.receipts?.[month] || 0
      return {
        month: new Date(currentYear, month).toLocaleDateString('en-US', { month: 'short' }),
        income: monthIncome,
        bills: monthBills,
        receipts: monthReceipts,
        net: monthIncome - monthBills
      }
    })

    return {
      totalIncome: safeTotals.totalIncome || 0,
      totalExpenses: safeTotals.totalExpenses || 0,
      totalBills: safeTotals.totalBills || 0,
      totalReceipts: safeTotals.totalReceipts || 0,
      totalProperties: safeTotals.totalProperties || 0,
      propertiesWithCoordinates: safeTotals.propertiesWithCoordinates || 0,
      outstandingAmount: safeTotals.outstanding || 0,
      collectionRate: safeTotals.collectionRate || 0,
      netRevenue: (safeTotals.totalIncome || 0) - (safeTotals.totalExpenses || 0),
      monthlyData
    }
  }, [dashboardData])

  // Chart data
  const monthlyChartData = useMemo(() => ({
    labels: metrics.monthlyData.map(item => item.month),
    datasets: [
      {
        label: 'Income',
        data: metrics.monthlyData.map(item => item.income),
        backgroundColor: 'rgba(34, 197, 94, 0.2)',
        borderColor: 'rgb(34, 197, 94)',
        borderWidth: 2,
        fill: true
      },
      {
        label: 'Bills Issued',
        data: metrics.monthlyData.map(item => item.bills),
        backgroundColor: 'rgba(239, 68, 68, 0.2)',
        borderColor: 'rgb(239, 68, 68)',
        borderWidth: 2,
        fill: true
      },
      {
        label: 'Receipts',
        data: metrics.monthlyData.map(item => item.receipts),
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 2,
        fill: true
      }
    ]
  }), [metrics.monthlyData])

  const collectionRateData = useMemo(() => ({
    labels: ['Collected', 'Outstanding'],
    datasets: [{
      data: [metrics.collectionRate, 100 - metrics.collectionRate],
      backgroundColor: ['#22c55e', '#ef4444'],
      borderWidth: 0
    }]
  }), [metrics.collectionRate])

  const propertyTypeCounts = useMemo(
    () => dashboardData.totals?.propertyTypeCounts || { RES: 0, COM: 0, IND: 0, OTH: 0 },
    [dashboardData.totals?.propertyTypeCounts]
  )

  const propertyTypeData = useMemo(() => ({
    labels: ['Residential', 'Commercial', 'Industrial', 'Other'],
    datasets: [{
      data: [
        propertyTypeCounts.RES,
        propertyTypeCounts.COM,
        propertyTypeCounts.IND,
        propertyTypeCounts.OTH,
      ],
      backgroundColor: ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'],
      borderWidth: 0
    }]
  }), [propertyTypeCounts])

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context) => ` ${context.dataset.label}: ${formatToZMW(context.raw)}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (value) => formatToZMW(value) }
      }
    }
  }

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0)
            const percentage = ((context.raw / total) * 100).toFixed(1)
            return `${context.label}: ${context.raw} (${percentage}%)`
          }
        }
      }
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-100">
        <FaSpinner className="animate-spin text-blue-950 text-4xl mb-4" />
        <p className="text-gray-600 font-medium">Loading dashboard...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-950 to-blue-800 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-4">
          <div className="bg-white/20 rounded-xl p-3">
            <FaChartLine className="text-2xl" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Revenue Dashboard</h1>
            <p className="text-blue-100 text-sm mt-1">Overview of revenue collection and property management</p>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Revenue"
          value={metrics.totalIncome}
          icon={FaDollarSign}
          color="green"
          trend={metrics.netRevenue > 0 ? 'up' : 'down'}
        />
        <MetricCard
          title="Outstanding Amount"
          value={metrics.outstandingAmount}
          icon={FaExclamationTriangle}
          color="red"
          trend={metrics.outstandingAmount > 0 ? 'up' : 'down'}
        />
        <MetricCard
          title="Collection Rate"
          value={`${metrics.collectionRate.toFixed(1)}%`}
          icon={FaChartLine}
          color="blue"
          trend={metrics.collectionRate > 70 ? 'up' : 'down'}
        />
        <MetricCard
          title="Total Properties"
          value={metrics.totalProperties}
          icon={FaBuilding}
          color="purple"
          subtitle={`${metrics.propertiesWithCoordinates} with coordinates`}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Trend */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <FaChartLine className="text-blue-600" />
            <h3 className="text-lg font-bold text-gray-900">Monthly Revenue Trend</h3>
          </div>
          <div className="h-80">
            <Line data={monthlyChartData} options={chartOptions} />
          </div>
        </div>

        {/* Collection Rate */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <FaCheckCircle className="text-green-600" />
            <h3 className="text-lg font-bold text-gray-900">Collection Rate</h3>
          </div>
          <div className="h-80">
            <Doughnut data={collectionRateData} options={pieChartOptions} />
          </div>
        </div>
      </div>

      {/* Property Distribution and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Property Types */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <FaBuilding className="text-purple-600" />
            <h3 className="text-lg font-bold text-gray-900">Property Distribution</h3>
          </div>
          <div className="h-80">
            <Pie data={propertyTypeData} options={pieChartOptions} />
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <FaUsers className="text-blue-600" />
            <h3 className="text-lg font-bold text-gray-900">Recent Activity</h3>
          </div>
          <div className="space-y-3">
            <ActivityItem
              icon={FaReceipt}
              title="Recent Receipts"
              count={dashboardData.totals?.recentReceipts?.count || 0}
              amount={dashboardData.totals?.recentReceipts?.amount || 0}
              color="green"
            />
            <ActivityItem
              icon={FaFileInvoice}
              title="Recent Bills"
              count={dashboardData.totals?.recentBills?.count || 0}
              amount={dashboardData.totals?.recentBills?.amount || 0}
              color="red"
            />
            <ActivityItem
              icon={FaBuilding}
              title="Properties Added"
              count={dashboardData.totals?.totalProperties || 0}
              color="blue"
            />
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 rounded-2xl border border-gray-200">
        <div className="flex items-center gap-2 mb-6">
          <FaChartLine className="text-blue-600" />
          <h3 className="text-lg font-bold text-gray-900">Summary Statistics</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-xl border border-gray-200 text-center hover:shadow-md transition-shadow">
            <div className="flex items-center justify-center gap-2 mb-2">
              <FaFileInvoice className="text-red-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatToZMW(metrics.totalBills)}
            </div>
            <div className="text-sm text-gray-600 mt-1">Total Bills Issued</div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200 text-center hover:shadow-md transition-shadow">
            <div className="flex items-center justify-center gap-2 mb-2">
              <FaReceipt className="text-green-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatToZMW(metrics.totalReceipts)}
            </div>
            <div className="text-sm text-gray-600 mt-1">Total Collections</div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200 text-center hover:shadow-md transition-shadow">
            <div className="flex items-center justify-center gap-2 mb-2">
              <FaExclamationTriangle className="text-orange-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatToZMW(metrics.outstandingAmount)}
            </div>
            <div className="text-sm text-gray-600 mt-1">Outstanding Balance</div>
          </div>
        </div>
      </div>
    </div>
  )
}

function MetricCard({ title, value, icon: Icon, color, trend, subtitle }) {
  const colorClasses = {
    green: { bg: 'bg-green-100', icon: 'text-green-600', border: 'border-green-200' },
    red: { bg: 'bg-red-100', icon: 'text-red-600', border: 'border-red-200' },
    blue: { bg: 'bg-blue-100', icon: 'text-blue-600', border: 'border-blue-200' },
    purple: { bg: 'bg-purple-100', icon: 'text-purple-600', border: 'border-purple-200' }
  }

  const colorScheme = colorClasses[color] || colorClasses.blue

  return (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
          <p className="text-3xl font-bold text-gray-900">
            {typeof value === 'number' ? formatToZMW(value) : value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-2">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorScheme.bg} ${colorScheme.border} border-2`}>
          <Icon className={`h-6 w-6 ${colorScheme.icon}`} />
        </div>
      </div>
      {trend && (
        <div className="mt-4 pt-4 border-t border-gray-200 flex items-center">
          {trend === 'up' ? (
            <div className="flex items-center gap-1.5">
              <FaArrowUp className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-600">Good</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <FaArrowDown className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-600">Needs Attention</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ActivityItem({ icon: Icon, title, count, amount, color }) {
  const colorClasses = {
    green: { bg: 'bg-green-100', icon: 'text-green-600', text: 'text-green-700' },
    red: { bg: 'bg-red-100', icon: 'text-red-600', text: 'text-red-700' },
    blue: { bg: 'bg-blue-100', icon: 'text-blue-600', text: 'text-blue-700' }
  }

  const colorScheme = colorClasses[color] || colorClasses.blue

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${colorScheme.bg}`}>
          <Icon className={`h-5 w-5 ${colorScheme.icon}`} />
        </div>
        <div>
          <p className="font-semibold text-gray-900">{title}</p>
          <p className="text-sm text-gray-600">{count} item{count !== 1 ? 's' : ''}</p>
        </div>
      </div>
      {amount !== undefined && (
        <div className="text-right">
          <p className="text-lg font-bold text-gray-900">{formatToZMW(amount)}</p>
        </div>
      )}
    </div>
  )
}

export default RevenueDashboard